import { useCallback, useEffect, useState } from 'react';
import { settingsApi } from '@/features/ai-dart/api/settings';
import { ConfigLines } from '@/features/ai-dart/format';
import { SOURCE_KINDS, type Source } from '@/features/ai-dart/types';
import { ActionButton, ActiveDot, TABLE, TD, TH, THEAD, TR } from '@/features/ai-dart/components/controls';
import { ConfirmModal, Modal } from '@/shared/ui/Modal';
import { Field, FieldRow, FormActions, FormError, FormNote, Input, Select, Textarea } from '@/features/ai-dart/components/form';

type Form = {
  id: number | null;
  source: string;
  kind: string;
  authKind: Source['authKind'];
  configText: string;
  token: string;
};

const EMPTY: Form = { id: null, source: '', kind: 'gcp-logs', authKind: 'none', configText: '', token: '' };

const formOf = (row: Source): Form => ({
  id: row.id, source: row.source, kind: row.kind, authKind: row.authKind,
  configText: ConfigLines.format(row.config), token: '',
});

/**
 * 폼 → 서버 payload. `source`는 **등록할 때만** 보낸다 — 서버 `update`가 받지 않는 필드다.
 *
 * `authKind`를 env_token 밖으로 명시 전환하면 서버가 `tokenEnc=NULL`을 탄다(의도된 폐기).
 * 그래서 토큰은 env_token일 때만, 그리고 값이 있을 때만 싣는다.
 */
function payloadOf(form: Form, config: Record<string, string>): Record<string, unknown> {
  const payload: Record<string, unknown> = { kind: form.kind.trim(), authKind: form.authKind, config };
  if (form.id === null) payload.source = form.source.trim();
  if (form.authKind === 'env_token' && form.token.trim()) payload.token = form.token;
  return payload;
}

export function SourcesView(): React.JSX.Element {
  const [rows, setRows] = useState<Source[]>([]); const [message, setMessage] = useState('');
  const load = useCallback(() => settingsApi.sources.list().then(setRows).catch((error: Error) => setMessage(error.message)), []);
  useEffect(() => { void load(); }, [load]);

  const [form, setForm] = useState<Form | null>(null);
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<Source | null>(null);

  const set = (patch: Partial<Form>): void => setForm((current) => (current ? { ...current, ...patch } : current));
  const run = async (work: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try { await work(); setMessage('저장했습니다.'); load(); }
    catch (error) { setMessage((error as Error).message); }
    finally { setBusy(false); }
  };

  const save = async (): Promise<void> => {
    if (!form) return;
    // `=` 없는 줄은 저장을 막는다 — 조용히 버리면 사용자는 반영된 줄 알고 넘어간다.
    const parsed = ConfigLines.parse(form.configText);
    if (parsed.invalid.length > 0) {
      setFormError(`연결 정보는 한 줄에 key=value로 적습니다. 형식이 아닌 줄: ${parsed.invalid.join(' / ')}`);
      return;
    }
    setBusy(true); setFormError('');
    try {
      const payload = payloadOf(form, parsed.config);
      await (form.id ? settingsApi.sources.update(form.id, payload) : settingsApi.sources.create(payload));
      setForm(null); setMessage('저장했습니다.'); load();
    } catch (error) { setFormError((error as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-lg border border-line bg-surface">
      <header className="flex items-center gap-3 border-b border-line p-4">
        <div>
          <h2 className="text-[13px] font-bold">인커밍 어댑터</h2>
          <p className="text-[11px] text-amber-700">enabled·config는 즉시 반영됩니다. 토큰은 현재 collector가 DB 값을 소비하지 않는 보관값이며, 실제 자격증명은 배포 env가 정본입니다.</p>
        </div>
        <div className="ml-auto">
          <ActionButton tone="primary" onClick={() => { setFormError(''); setForm(EMPTY); }}>＋ 어댑터 등록</ActionButton>
        </div>
      </header>
      {message && <p className="border-b border-line bg-line-soft p-2 px-4 text-[12px]">{message}</p>}
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th className={TH}>활성</th>
              <th className={TH}>소스</th>
              <th className={TH}>수집기</th>
              <th className={TH}>인증</th>
              <th className={TH}>토큰</th>
              <th className={`${TH} text-right`}>조작</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={TR}>
                {/* 활성 표현을 모델 화면과 같은 초록 점으로 맞춘다 — 두 화면이 같은 뜻을 다르게 그리면 읽는 사람이 매번 다시 배운다. */}
                <td className={TD}><ActiveDot on={row.enabled} label={row.enabled ? '활성' : '비활성'} /></td>
                <td className={`${TD} font-medium`}>{row.source}</td>
                <td className={`${TD} text-muted`}>{row.kind}</td>
                <td className={TD}><span className="rounded bg-line-soft px-2 py-0.5 text-[11px]">{row.authKind}</span></td>
                <td className={`${TD} text-muted`}>{row.hasToken ? row.tokenHint ?? '토큰 저장됨' : '토큰 없음'}</td>
                <td className={TD}>
                  <div className="flex justify-end gap-1.5">
                    <ActionButton tone={row.enabled ? 'default' : 'primary'} disabled={busy} onClick={() => void run(() => settingsApi.sources.toggle(row.id))}>
                      {row.enabled ? '비활성' : '활성'}
                    </ActionButton>
                    <ActionButton onClick={() => { setFormError(''); setForm(formOf(row)); }}>수정</ActionButton>
                    <ActionButton tone="danger" onClick={() => setRemoving(row)}>삭제</ActionButton>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="p-8 text-center text-muted" colSpan={6}>등록된 어댑터가 없습니다.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={form !== null} onClose={() => setForm(null)} title={form?.id ? `어댑터 수정 · ${form.source}` : '어댑터 등록'}>
        {form && (
          <div>
            {/* 소스 이름은 서버 `update`가 받지 않는다 — 수정에서 입력칸을 주면 고칠 수 있는 것처럼 보인다. */}
            {form.id === null ? (
              <Field label="소스 이름 (고유)">
                <Input autoFocus value={form.source} onChange={(event) => set({ source: event.target.value })} placeholder="예: sentry-market" />
              </Field>
            ) : (
              <Field label="소스 이름" hint="이름은 등록 후 바꿀 수 없습니다.">
                <Input value={form.source} disabled />
              </Field>
            )}
            <FieldRow>
              <Field label="수집기 종류">
                <Select value={form.kind} onChange={(event) => set({ kind: event.target.value })}>
                  {/* 저장된 값이 목록 밖이면 옵션으로 얹는다 — 그러지 않으면 셀렉트가 조용히 첫 항목으로 바꾼다. */}
                  {!SOURCE_KINDS.includes(form.kind) && <option value={form.kind}>{form.kind} (현재)</option>}
                  {SOURCE_KINDS.map((kind) => <option key={kind} value={kind}>{kind}</option>)}
                </Select>
              </Field>
              <Field label="인증 방식">
                <Select value={form.authKind} onChange={(event) => set({ authKind: event.target.value as Source['authKind'] })}>
                  <option value="none">없음</option>
                  <option value="workload_identity">Workload Identity</option>
                  <option value="env_token">토큰(env)</option>
                </Select>
              </Field>
            </FieldRow>
            <Field
              label="연결 정보 (한 줄에 key=value)"
              hint="값이 빈 키도 그대로 유지됩니다. 값에 = 이 들어가도 첫 = 만 구분자로 씁니다."
            >
              <Textarea
                value={form.configText}
                onChange={(event) => set({ configText: event.target.value })}
                placeholder={'org=my-org\nprojects=api,web'}
              />
            </Field>
            {form.authKind === 'env_token' && (
              <>
                <Field label="토큰 (선택)">
                  <Input
                    type="password"
                    autoComplete="off"
                    value={form.token}
                    onChange={(event) => set({ token: event.target.value })}
                    placeholder={form.id ? '변경할 때만 입력' : '비워 두면 저장하지 않음'}
                  />
                </Field>
                <FormNote>
                  🔑 실제 자격증명의 <b>정본은 배포 env</b>(Secret Manager 주입)입니다. 여기 저장하는 값은 봉투 암호화된
                  보관값이고 현재 collector가 소비하지 않습니다 — 비워 두어도 수집은 동작합니다.
                </FormNote>
              </>
            )}
            {form.id !== null && form.authKind !== 'env_token' && rows.find((row) => row.id === form.id)?.hasToken && (
              <FormNote>⚠ 인증 방식을 토큰(env) 밖으로 바꿔 저장하면 <b>저장된 토큰이 삭제됩니다</b>.</FormNote>
            )}
            {formError && <FormError message={formError} />}
            <FormActions>
              <ActionButton tone="primary" disabled={busy || (form.id === null && !form.source.trim())} onClick={() => void save()}>
                {form.id ? '저장' : '등록'}
              </ActionButton>
              <ActionButton onClick={() => setForm(null)}>취소</ActionButton>
            </FormActions>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={removing !== null}
        title="어댑터 삭제"
        confirmLabel="삭제"
        busy={busy}
        onClose={() => setRemoving(null)}
        onConfirm={() => { const row = removing; setRemoving(null); if (row) void run(() => settingsApi.sources.remove(row.id)); }}
      >
        <strong>{removing?.source}</strong>({removing?.kind})을 삭제합니다. 이 경로로 들어오던 인입이 멈춥니다.
      </ConfirmModal>
    </section>
  );
}
